import { useEffect, useState } from 'react';
import { Link, NavLink, Outlet } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  SignInButton,
  SignUpButton,
  UserButton,
  useAuth,
  useUser
} from '@clerk/react';
import {
  CreditCard,
  HelpCircle,
  Home,
  List,
  Loader2,
  Mail,
  MapPin,
  Tags,
  Shield
} from 'lucide-react';
import { useToast } from '@/shared/contexts/ToastContext';
import { ACCOUNT_ACCENT, accountModalAuthButtonClass } from './accountStyles';
import {
  fetchAssignedLeads,
  isBrokerMaxAdminUserId,
  submitSupportRequest
} from './api/accountApi';
import { useWallet } from './hooks/useWallet';

const clerkPublishableKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;
const whiteLogoSrc =
  (import.meta.env.VITE_WHITE_LOGO_URL as string | undefined) ||
  '/images/logo-white.png';
const fallbackLogoSrc = '/favicon.svg';

const sidebarNav = [
  { to: '/account', label: 'Dashboard', icon: Home },
  { to: '/account/leads', label: 'My Leads', icon: List }
] as const;

const sidebarNavAfterPurchase = [
  { to: '/account/territory', label: 'My Territory', icon: MapPin },
  { to: '/account/billing', label: 'Billing', icon: CreditCard }
] as const;

const purchaseNav = [
  { to: '/account/purchase-leads', label: 'Premium Leads', icon: Tags },
  { to: '/account/clearance', label: 'Clearance', icon: Tags }
] as const;

const AccountLayout = () => {
  const { notify } = useToast();
  const { isLoaded, userId, sessionId, getToken } = useAuth();
  const { user } = useUser();
  const [loadTakingLong, setLoadTakingLong] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [logoSrc, setLogoSrc] = useState(whiteLogoSrc);
  const [logoError, setLogoError] = useState(false);
  const [helpName, setHelpName] = useState('');
  const [helpEmail, setHelpEmail] = useState('');
  const [helpMessage, setHelpMessage] = useState('');
  const [helpSubmitting, setHelpSubmitting] = useState(false);
  const { data: wallet, isLoading: walletLoading } = useWallet();
  const { data: myLeads = [] } = useQuery({
    queryKey: ['account', 'leads', userId, sessionId, getToken],
    queryFn: () => fetchAssignedLeads(getToken),
    enabled: Boolean(userId),
    staleTime: 30_000,
    refetchOnWindowFocus: false
  });

  useEffect(() => {
    if (isLoaded) {
      setLoadTakingLong(false);
      return;
    }
    if (!clerkPublishableKey) return;
    const t = window.setTimeout(() => setLoadTakingLong(true), 10_000);
    return () => window.clearTimeout(t);
  }, [isLoaded]);

  const openHelpCenter = () => {
    const fullName =
      [user?.firstName ?? '', user?.lastName ?? ''].join(' ').trim() ||
      user?.username ||
      '';
    const email = user?.primaryEmailAddress?.emailAddress ?? '';
    setHelpName(fullName);
    setHelpEmail(email);
    setHelpMessage('');
    setHelpOpen(true);
  };

  const submitHelpRequest = async () => {
    if (!helpMessage.trim()) {
      notify('error', 'Please enter a message.');
      return;
    }
    setHelpSubmitting(true);
    try {
      await submitSupportRequest(getToken, {
        message: helpMessage.trim(),
        name: helpName.trim() || undefined,
        email: helpEmail.trim() || undefined
      });
      notify(
        'success',
        'Thanks — we have received your message and will follow up shortly.'
      );
      setHelpOpen(false);
    } catch (e) {
      notify(
        'error',
        e instanceof Error ? e.message : 'Could not send your message.'
      );
    } finally {
      setHelpSubmitting(false);
    }
  };

  if (!clerkPublishableKey) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-[#eef1f4] px-4 font-[var(--font-Inter),system-ui,sans-serif]">
        <div className="max-w-lg rounded-xl border border-slate-200 bg-white p-8 text-center shadow-sm">
          <h1 className="text-lg font-semibold text-slate-900">
            Clerk is not configured
          </h1>
          <p className="mt-2 text-sm text-slate-600">
            Add{' '}
            <code className="rounded bg-slate-100 px-1.5 py-0.5 text-xs">
              VITE_CLERK_PUBLISHABLE_KEY
            </code>{' '}
            to{' '}
            <code className="rounded bg-slate-100 px-1.5 py-0.5 text-xs">
              .env.local
            </code>
            , then restart{' '}
            <code className="rounded bg-slate-100 px-1.5 py-0.5 text-xs">
              npm run dev
            </code>
            .
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col font-[var(--font-Inter),system-ui,sans-serif]">
      <header
        className={`${ACCOUNT_ACCENT.bar} flex h-12 shrink-0 items-center justify-between px-4 md:px-6`}
      >
        <Link
          to="/account"
          className="flex items-center gap-2 text-white/95 transition-colors hover:text-white"
        >
          {!logoError ? (
            <img
              src={logoSrc}
              alt="BrokerMax"
              className="h-7 w-auto object-contain"
              onError={() => {
                if (logoSrc !== fallbackLogoSrc) {
                  setLogoSrc(fallbackLogoSrc);
                  return;
                }
                setLogoError(true);
              }}
            />
          ) : (
            <>
              <span className="flex h-8 w-8 items-center justify-center rounded-md bg-white/10 text-sm font-bold tracking-tight">
                B
              </span>
              <span className="hidden text-sm font-semibold tracking-tight sm:inline">
                BrokerMax
              </span>
            </>
          )}
          <span className="hidden text-sm font-semibold tracking-tight sm:inline">
            My Account
          </span>
        </Link>
        <div className="flex items-center gap-1 sm:gap-4">
          {!isLoaded ? (
            <span className="inline-flex items-center gap-2 text-xs text-white/60">
              <Loader2
                className="h-4 w-4 shrink-0 animate-spin"
                strokeWidth={2}
                aria-hidden
              />
              Loading…
            </span>
          ) : userId ? (
            <>
              <Link
                to="/account/help-center"
                className="hidden items-center gap-1.5 rounded-md px-2 py-1.5 text-xs font-medium text-white/80 hover:bg-white/10 hover:text-white md:flex"
              >
                <HelpCircle className="h-4 w-4" strokeWidth={1.5} />
                Help center
              </Link>
              <button
                type="button"
                onClick={openHelpCenter}
                className="hidden items-center gap-1.5 rounded-md px-2 py-1.5 text-xs font-medium text-white/80 hover:bg-white/10 hover:text-white md:flex"
              >
                <Mail className="h-4 w-4" strokeWidth={1.5} />
                Email Support
              </button>
              <div className="hidden items-center gap-2 sm:flex">
                <div className="inline-flex items-center gap-1.5 rounded-md px-2 py-1.5 text-xs font-medium text-white/80">
                  <CreditCard className="h-4 w-4" strokeWidth={1.5} />
                  Wallet{' '}
                  {walletLoading
                    ? '…'
                    : `$${(wallet?.balance ?? 0).toFixed(2)}`}
                </div>
                <Link
                  to="/account?focus=topup"
                  className="inline-flex items-center gap-1.5 rounded-md px-2 py-1.5 text-xs font-medium text-white/80 hover:bg-white/10 hover:text-white"
                >
                  <CreditCard className="h-4 w-4" strokeWidth={1.5} />
                  Top up
                </Link>
              </div>
              <div className="flex items-center gap-2 border-l border-white/15 pl-3 sm:pl-4">
                <span className="hidden text-xs font-medium text-white/80 lg:inline">
                  Me
                </span>
                <UserButton
                  appearance={{
                    elements: {
                      avatarBox: 'h-8 w-8 ring-2 ring-white/20'
                    }
                  }}
                />
              </div>
            </>
          ) : (
            <div className="flex items-center gap-2">
              <SignInButton mode="modal">
                <button
                  type="button"
                  className="rounded-md px-3 py-1.5 text-xs font-semibold text-white/90 hover:bg-white/10"
                >
                  Sign in
                </button>
              </SignInButton>
              <SignUpButton mode="modal">
                <button
                  type="button"
                  className="rounded-md bg-white/15 px-3 py-1.5 text-xs font-semibold text-white hover:bg-white/25"
                >
                  Sign up
                </button>
              </SignUpButton>
            </div>
          )}
        </div>
      </header>

      {!isLoaded ? (
        <div
          className={`flex flex-1 flex-col items-center justify-center gap-3 px-4 py-24 ${ACCOUNT_ACCENT.pageBg}`}
        >
          <Loader2
            className="h-8 w-8 animate-spin text-[#2b6cff]"
            strokeWidth={2}
            aria-hidden
          />
          <p className="text-sm text-slate-600">Loading your account…</p>
          {loadTakingLong ? (
            <div className="mt-4 max-w-md rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-left text-xs text-amber-950">
              <p className="font-semibold">Still loading?</p>
              <ul className="mt-2 list-inside list-disc space-y-1 text-amber-900/90">
                <li>
                  Restart the dev server after changing{' '}
                  <code className="rounded bg-amber-100/80 px-1">
                    .env.local
                  </code>
                  .
                </li>
                <li>
                  In the Clerk dashboard, allow this origin:{' '}
                  <code className="rounded bg-amber-100/80 px-1">
                    http://localhost:3000
                  </code>{' '}
                  (Configure → Domains / Paths).
                </li>
                <li>
                  Check the browser console for Clerk errors (blocked network or
                  ad blockers).
                </li>
              </ul>
            </div>
          ) : null}
        </div>
      ) : !userId ? (
        <div
          className={`flex flex-1 flex-col items-center justify-center px-4 py-16 ${ACCOUNT_ACCENT.pageBg}`}
        >
          <div className="w-full max-w-md rounded-2xl border border-slate-200/80 bg-white p-10 text-center shadow-sm">
            <h1 className="text-2xl font-bold text-slate-900">Agent account</h1>
            <p className="mt-2 text-sm text-slate-600">
              Sign in to open your dashboard, wallet, and lead feed.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:justify-center">
              <SignInButton mode="modal">
                <button type="button" className={accountModalAuthButtonClass}>
                  Sign in
                </button>
              </SignInButton>
              <SignUpButton mode="modal">
                <button
                  type="button"
                  className="rounded-lg border border-slate-200 bg-white px-5 py-2.5 text-sm font-semibold text-slate-800 hover:bg-slate-50"
                >
                  Create account
                </button>
              </SignUpButton>
            </div>
            <Link
              to="/"
              className="mt-8 inline-block text-sm font-medium text-[#2b6cff] hover:underline"
            >
              ← Back to home
            </Link>
          </div>
        </div>
      ) : (
        <div className="flex min-h-0 flex-1">
          <aside className="flex w-14 shrink-0 flex-col border-r border-slate-300/50 bg-[#f0f4fa]/95 md:w-[220px]">
            <nav className="flex flex-1 flex-col gap-0.5 py-4">
              {sidebarNav.map(item => {
                const Icon = item.icon;
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.to === '/account'}
                    className={({ isActive }) =>
                      `mx-2 flex items-center gap-3 rounded-r-md py-2.5 pl-3 pr-2 text-left transition-colors md:mx-3 ${
                        isActive
                          ? `${ACCOUNT_ACCENT.activeSidebar} text-[#2b6cff]`
                          : 'border-l-[3px] border-transparent text-slate-500 hover:bg-slate-50 hover:text-slate-800'
                      }`
                    }
                  >
                    <Icon className="h-5 w-5 shrink-0" strokeWidth={1.5} />
                    <span className="hidden text-sm font-medium md:inline">
                      {item.label}
                    </span>
                    {item.to === '/account/leads' ? (
                      <span className="ml-auto hidden rounded-full bg-slate-200 px-2 py-0.5 text-[11px] font-semibold text-slate-700 md:inline">
                        {myLeads.length}
                      </span>
                    ) : null}
                  </NavLink>
                );
              })}

              <div className="mx-2 mt-2 rounded-lg border border-slate-200/80 bg-white/60 px-2 py-2 md:mx-3">
                <div className="hidden px-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500 md:block">
                  Purchase Leads
                </div>
                {purchaseNav.map(item => {
                  const Icon = item.icon;
                  return (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      className={({ isActive }) =>
                        `mt-0.5 flex items-center gap-2 rounded-md py-2 pl-2 pr-2 text-left transition-colors ${
                          isActive
                            ? 'bg-[#eef4ff] text-[#2b6cff]'
                            : 'text-slate-600 hover:bg-slate-50 hover:text-slate-800'
                        }`
                      }
                    >
                      <Icon className="h-4 w-4 shrink-0" strokeWidth={1.75} />
                      <span className="hidden text-sm font-medium md:inline">
                        {item.label}
                      </span>
                    </NavLink>
                  );
                })}
              </div>

              {sidebarNavAfterPurchase.map(item => {
                const Icon = item.icon;
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    className={({ isActive }) =>
                      `mx-2 flex items-center gap-3 rounded-r-md py-2.5 pl-3 pr-2 text-left transition-colors md:mx-3 ${
                        isActive
                          ? `${ACCOUNT_ACCENT.activeSidebar} text-[#2b6cff]`
                          : 'border-l-[3px] border-transparent text-slate-500 hover:bg-slate-50 hover:text-slate-800'
                      }`
                    }
                  >
                    <Icon className="h-5 w-5 shrink-0" strokeWidth={1.5} />
                    <span className="hidden text-sm font-medium md:inline">
                      {item.label}
                    </span>
                  </NavLink>
                );
              })}

              {isBrokerMaxAdminUserId(userId) || import.meta.env.DEV ? (
                <div className="mt-auto pt-3">
                  <NavLink
                    to="/account/el"
                    className={({ isActive }) =>
                      `mx-2 mt-2 flex items-center gap-3 rounded-r-md py-2.5 pl-3 pr-2 text-left transition-colors md:mx-3 ${
                        isActive
                          ? `${ACCOUNT_ACCENT.activeSidebar} text-[#2b6cff]`
                          : 'border-l-[3px] border-transparent text-slate-500 hover:bg-slate-50 hover:text-slate-800'
                      }`
                    }
                  >
                    <Shield className="h-5 w-5 shrink-0" strokeWidth={1.5} />
                    <span className="hidden text-sm font-medium md:inline">
                      EL Leads
                    </span>
                  </NavLink>
                  <NavLink
                    to="/account/el-accounts"
                    className={({ isActive }) =>
                      `mx-2 flex items-center gap-3 rounded-r-md py-2.5 pl-3 pr-2 text-left transition-colors md:mx-3 ${
                        isActive
                          ? `${ACCOUNT_ACCENT.activeSidebar} text-[#2b6cff]`
                          : 'border-l-[3px] border-transparent text-slate-500 hover:bg-slate-50 hover:text-slate-800'
                      }`
                    }
                  >
                    <Shield className="h-5 w-5 shrink-0" strokeWidth={1.5} />
                    <span className="hidden text-sm font-medium md:inline">
                      EL Accounts
                    </span>
                  </NavLink>
                </div>
              ) : null}
            </nav>
          </aside>

          <main
            className={`relative flex-1 overflow-auto ${ACCOUNT_ACCENT.pageBg} ${ACCOUNT_ACCENT.pageWash} px-4 py-6 md:px-8 md:py-8`}
          >
            <Outlet />
          </main>
        </div>
      )}
      {helpOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/45 p-4">
          <div className="w-full max-w-lg rounded-2xl border border-slate-200 bg-white p-5 shadow-2xl">
            <div className="mb-4">
              <h2 className="text-lg font-semibold text-slate-900">
                Email Support
              </h2>
              <p className="mt-1 text-sm text-slate-600">
                Send us a message and we will get back to you.
              </p>
            </div>
            <div className="grid gap-3">
              <label className="text-xs font-medium text-slate-600">
                Name
                <input
                  value={helpName}
                  onChange={e => setHelpName(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-900 outline-none ring-[#2b6cff]/20 focus:ring-4"
                />
              </label>
              <label className="text-xs font-medium text-slate-600">
                Email
                <input
                  value={helpEmail}
                  onChange={e => setHelpEmail(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-900 outline-none ring-[#2b6cff]/20 focus:ring-4"
                />
              </label>
              <label className="text-xs font-medium text-slate-600">
                Message
                <textarea
                  value={helpMessage}
                  onChange={e => setHelpMessage(e.target.value)}
                  rows={6}
                  placeholder="How can we help?"
                  className="mt-1 w-full resize-y rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-900 outline-none ring-[#2b6cff]/20 focus:ring-4"
                />
              </label>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setHelpOpen(false)}
                className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={helpSubmitting}
                onClick={() => void submitHelpRequest()}
                className="rounded-lg bg-[#2b6cff] px-4 py-2 text-sm font-semibold text-white hover:bg-[#1f5ef7] disabled:opacity-60"
              >
                {helpSubmitting ? 'Sending…' : 'Send message'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default AccountLayout;
